import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type Task, type Assignment, type Employee, bulkUpdateTaskDue } from '../../lib/api'
import { ArrowUp, ArrowDown, Calendar, ArrowRightLeft, X, Check } from 'lucide-react'
import { AssigneeControl } from '../assignment/AssigneeControl'
import { StatusSelect, DueDateInput, ClientLabel, CompleteToggle } from './TaskControls'
import { sortTasks, type SortKey, type SortDir } from './taskViews'

interface TaskListProps {
  tasks: Task[]
  assignmentsByTask?: Map<string, Assignment[]>
  employees?: Employee[]
  /**
   * May edit task fields. This is `roleCanWrite`, not `roleCanManage` — a
   * member is allowed to work the tasks they're assigned to, and the API
   * agrees (`requireWrite` lets them through). Only `read_only` is locked out.
   */
  canWrite?: boolean
  /**
   * May change who is assigned. `roleCanWrite`, same as editing: a member can
   * hand off a task they're on to anyone on the team (the API holds them to
   * tasks they're assigned to — `src/shared/assignment-scope.ts`).
   */
  canAssign?: boolean
  /** Signed-in employee — enables the hand-off rules in `assignment/handoff.ts`. */
  currentEmployeeId?: string | null
  /** The signed-in role only sees tasks it's assigned to. */
  scopedToAssigned?: boolean
  /** Whether client names link through to the client record. */
  canOpenClients?: boolean
  onTaskClick?: (taskId: string) => void
  /** Enable bulk selection and actions (list view only) */
  bulkSelectable?: boolean
}

export function TaskList({
  tasks,
  assignmentsByTask,
  employees = [],
  canWrite = true,
  canAssign = true,
  canOpenClients = false,
  currentEmployeeId = null,
  scopedToAssigned = false,
  onTaskClick,
  bulkSelectable = false,
}: TaskListProps) {
  const queryClient = useQueryClient()
  // Due date ascending is the Monday-morning triage order: what's late, then
  // what's next.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'due', dir: 'asc' })
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Bulk action mode: 'set' | 'shift' | null
  const [bulkMode, setBulkMode] = useState<'set' | 'shift' | null>(null)
  // For shift mode: how many days to shift
  const [dayShiftValue, setDayShiftValue] = useState<number>(0)
  // For set mode: the target date
  const [setDueDate, setSetDueDate] = useState<string>('')

  const toggleSelect = (taskId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) next.delete(taskId)
      else next.add(taskId)
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(new Set(tasks.map((t) => t.id)))
  }

  const clearSelection = () => {
    setSelectedIds(new Set())
    setBulkMode(null)
  }

  const bulkMutation = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selectedIds)
      if (bulkMode === 'set' && setDueDate) {
        return bulkUpdateTaskDue(ids, setDueDate, undefined)
      } else if (bulkMode === 'shift' && dayShiftValue !== 0) {
        return bulkUpdateTaskDue(ids, undefined, dayShiftValue)
      }
      throw new Error('Invalid bulk operation')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      clearSelection()
    },
  })

  const showBulkBar = bulkSelectable && selectedIds.size > 0

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))

  if (tasks.length === 0) {
    return <p className="empty-state">No tasks match your filters.</p>
  }

  const sorted = sortTasks(tasks, sort.key, sort.dir)

  const SortHeader = ({ label, sortKey, className }: { label: string; sortKey: SortKey; className: string }) => (
    <button
      className={`${className} task-table-sort${sort.key === sortKey ? ' task-table-sort--active' : ''}`}
      onClick={() => toggleSort(sortKey)}
      aria-label={`Sort by ${label.toLowerCase()}`}
    >
      {label}
      {sort.key === sortKey &&
        (sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
    </button>
  )

  // Bulk action bar
  const BulkActionBar = () => (
    <div className="bulk-action-bar">
      <span className="bulk-action-bar__count">{selectedIds.size} selected</span>
      <div className="bulk-action-bar__actions">
        <button
          className={`btn btn-sm${bulkMode === 'set' ? ' btn-primary' : ' btn-ghost'}`}
          onClick={() => setBulkMode('set')}
          title="Set due date"
        >
          <Calendar size={14} /> Set due
        </button>
        <button
          className={`btn btn-sm${bulkMode === 'shift' ? ' btn-primary' : ' btn-ghost'}`}
          onClick={() => setBulkMode('shift')}
          title="Shift due date"
        >
          <ArrowRightLeft size={14} /> Shift
        </button>
      </div>
      {bulkMode === 'set' && (
        <div className="bulk-action-bar__input">
          <input
            type="date"
            value={setDueDate}
            onChange={(e) => setSetDueDate(e.target.value)}
            aria-label="New due date"
          />
          <button
            className="btn btn-sm btn-primary"
            onClick={() => bulkMutation.mutate()}
            disabled={!setDueDate || bulkMutation.isPending}
          >
            <Check size={14} /> Apply
          </button>
        </div>
      )}
      {bulkMode === 'shift' && (
        <div className="bulk-action-bar__input">
          <input
            type="number"
            value={dayShiftValue}
            onChange={(e) => setDayShiftValue(parseInt(e.target.value) || 0)}
            min={-365}
            max={365}
            aria-label="Days to shift"
            style={{ width: '60px' }}
          />
          <span className="text-muted">days</span>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => bulkMutation.mutate()}
            disabled={dayShiftValue === 0 || bulkMutation.isPending}
          >
            <Check size={14} /> Apply
          </button>
        </div>
      )}
      <button className="btn btn-ghost btn-sm" onClick={clearSelection} title="Clear selection">
        <X size={14} />
      </button>
    </div>
  )

  return (
    <div className="task-table task-table--v2">
      {showBulkBar && <BulkActionBar />}
      <div className="task-table-header">
        {bulkSelectable && <span className="task-col-check" />}
        <SortHeader label="Title" sortKey="title" className="task-col-title" />
        <SortHeader label="Client" sortKey="client" className="task-col-client" />
        <SortHeader label="Status" sortKey="status" className="task-col-status" />
        <SortHeader label="Priority" sortKey="priority" className="task-col-priority" />
        <span className="task-col-assignees">Assignees</span>
        <SortHeader label="Due" sortKey="due" className="task-col-due" />
      </div>
      {sorted.map((task) => (
        <div key={task.id} className={`task-table-row${task.status === 'done' ? ' task-row--done' : ''}${selectedIds.has(task.id) ? ' task-table-row--selected' : ''}`}>
          {bulkSelectable && (
            <span className="task-col-check">
              <input
                type="checkbox"
                checked={selectedIds.has(task.id)}
                onChange={() => toggleSelect(task.id)}
                aria-label={`Select ${task.title}`}
              />
            </span>
          )}
          {!bulkSelectable && <CompleteToggle task={task} disabled={!canWrite} />}
          <span
            className="task-col-title task-title"
            title={task.title}
            onClick={() => onTaskClick?.(task.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onTaskClick?.(task.id) }}
          >
            {task.title}
          </span>
          <span className="task-col-client">
            <ClientLabel clientId={task.clientId} clientName={task.clientName} linkTo={canOpenClients} />
          </span>
          <span className="task-col-status">
            <StatusSelect task={task} disabled={!canWrite} />
          </span>
          <span className="task-col-priority">
            <span className={`priority-dot priority-${task.priority}`} />
            {task.priority}
          </span>
          <span className="task-col-assignees">
            <AssigneeControl
              entityType="task"
              entityId={task.id}
              assignments={assignmentsByTask?.get(task.id) ?? []}
              employees={employees}
              canManage={canAssign}
              compact
              invalidateKeys={['tasks']}
              currentEmployeeId={currentEmployeeId}
              scopedToAssigned={scopedToAssigned}
            />
          </span>
          <span className="task-col-due">
            <DueDateInput task={task} disabled={!canWrite} />
          </span>
        </div>
      ))}
    </div>
  )
}
